import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { AuthUser } from '../types';
import { requestAndRegisterToken } from '../services/notificationService';

const TOKEN_KEY = 'taxiLeadsToken';

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ requires2FA?: boolean; tempToken?: string }>;
  register: (name: string, email: string, password: string, referralCode?: string) => Promise<void>;
  handleGoogleToken: (token: string) => Promise<void>;
  verify2FA: (tempToken: string, code: string) => Promise<void>;
  logout: () => void;
  updateUser: (data: Partial<AuthUser>) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const storeSession = (newToken: string, newUser: AuthUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    setUser(newUser);
  };

  const clearSession = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) { setIsLoading(false); return; }

    setToken(storedToken);
    api.get('/api/auth/verify', { headers: { Authorization: `Bearer ${storedToken}` } })
      .then((res) => setUser(res.data.user))
      .catch(() => clearSession())
      .finally(() => setIsLoading(false));
  }, []);

  const login = async (email: string, password: string) => {
    const res = await api.post('/api/auth/login', { email, password });
    if (res.data.requires2FA) {
      return { requires2FA: true, tempToken: res.data.tempToken as string };
    }
    storeSession(res.data.token, res.data.user);
    requestAndRegisterToken().catch(() => {});
    return {};
  };

  const register = async (name: string, email: string, password: string, referralCode?: string) => {
    const res = await api.post('/api/auth/register', { name, email, password, ...(referralCode ? { referralCode } : {}) });
    storeSession(res.data.token, res.data.user);
    requestAndRegisterToken().catch(() => {});
  };

  const handleGoogleToken = async (token: string) => {
    const res = await api.get('/api/auth/verify', { headers: { Authorization: `Bearer ${token}` } });
    storeSession(token, res.data.user);
    requestAndRegisterToken().catch(() => {});
  };

  const verify2FA = async (tempToken: string, code: string) => {
    const res = await api.post('/api/auth/2fa/login', { tempToken, code });
    storeSession(res.data.token, res.data.user);
    requestAndRegisterToken().catch(() => {});
  };

  const logout = () => clearSession();

  const updateUser = (data: Partial<AuthUser>) => {
    setUser((prev) => (prev ? { ...prev, ...data } : prev));
  };

  const value = useMemo(
    () => ({ token, user, isLoading, login, register, handleGoogleToken, verify2FA, logout, updateUser }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, user, isLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
